import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { ROLES } from "../lib/constants";
import {
  LayoutDashboard, ClipboardList, Contact, Truck, Route as RouteIcon,
  Bell, LogOut, Menu, X, Users, Settings, Navigation, CalendarDays,
  Factory, Scissors, Ruler, Layers, Sofa, Wrench, Warehouse, ChevronDown,
} from "lucide-react";
import { Button } from "./ui/button";

const LOG = ["amministratore", "responsabile_logistica", "pm_produzione"];
const PROD = ["amministratore", "pm_produzione"];

const GROUPS = [
  {
    key: "logistica",
    label: "Logistica e giri",
    icon: RouteIcon,
    roles: LOG,
    items: [
      { to: "/", label: "Cruscotto", icon: LayoutDashboard, roles: LOG },
      { to: "/richieste", label: "Richieste", icon: ClipboardList, roles: LOG },
      { to: "/pianificazione", label: "Pianificazione giri", icon: RouteIcon, roles: ["amministratore", "responsabile_logistica"] },
      { to: "/calendario", label: "Calendario", icon: CalendarDays, roles: LOG },
      { to: "/anagrafica", label: "Anagrafica", icon: Contact, roles: LOG },
      { to: "/flotta", label: "Mezzi e autisti", icon: Truck, roles: ["amministratore", "responsabile_logistica"] },
    ],
  },
  {
    key: "produzione",
    label: "Produzione",
    icon: Factory,
    roles: PROD,
    items: [
      { to: "/produzione/taglio", label: "Taglio", icon: Scissors, roles: PROD },
      { to: "/produzione/cucito", label: "Cucito", icon: Ruler, roles: PROD },
      { to: "/produzione/rivestimento", label: "Rivestimento", icon: Layers, roles: PROD },
      { to: "/produzione/tappezzeria", label: "Tappezzeria", icon: Sofa, roles: PROD },
      { to: "/produzione/montaggio", label: "Montaggio", icon: Wrench, roles: PROD },
    ],
  },
  {
    key: "magazzino",
    label: "Magazzino",
    icon: Warehouse,
    roles: PROD,
    to: "/magazzino",
  },
];

const SINGLES = [
  { to: "/autista", label: "I miei Giri", icon: Navigation, roles: ["autista"] },
  { to: "/utenti", label: "Utenti", icon: Users, roles: ["amministratore"] },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, roles: ["amministratore", "responsabile_logistica"] },
];

const testidFor = (to) => `nav-${to.replace(/\//g, "-").replace(/^-/, "") || "home"}`;

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const [expanded, setExpanded] = useState({ logistica: true, produzione: true });

  const loadNotifs = async () => {
    try { const { data } = await api.get("/notifications"); setNotifs(data); } catch {}
  };
  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 20000);
    return () => clearInterval(t);
  }, []);

  const unread = notifs.filter((n) => !n.read).length;
  const visibleGroups = GROUPS
    .map((g) => (g.items ? { ...g, items: g.items.filter((i) => i.roles.includes(user?.role)) } : g))
    .filter((g) => g.roles.includes(user?.role) && (g.to || (g.items && g.items.length > 0)));
  const singles = SINGLES.filter((n) => n.roles.includes(user?.role));

  const markAll = async () => { await api.post("/notifications/read-all"); loadNotifs(); };

  const SideContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 py-6 border-b border-lm-sandsoft">
        <img src="/logo-mascheroni.webp" alt="Ludovica Mascheroni" className="h-9 w-auto object-contain" />
        <p className="mt-2 text-[10px] tracking-[0.25em] uppercase text-lm-muted font-semibold">Logistica &amp; Giri</p>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {visibleGroups.map((g) => {
          if (g.to) {
            return (
              <NavLink key={g.key} to={g.to} end onClick={() => setOpen(false)}
                data-testid={testidFor(g.to)}
                className={({ isActive }) =>
                  `w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
                    isActive ? "bg-lm-brown text-lm-cream" : "text-lm-muted hover:bg-lm-sandsoft"
                  }`
                }>
                <g.icon className="w-[18px] h-[18px]" />
                <span className="flex-1 text-left">{g.label}</span>
              </NavLink>
            );
          }
          const isOpen = expanded[g.key] !== false;
          const activeInGroup = g.items.some(
            (i) => location.pathname === i.to || (i.to !== "/" && location.pathname.startsWith(i.to)));
          return (
            <div key={g.key} className="mb-1">
              <button type="button" data-testid={`nav-group-${g.key}`}
                onClick={() => setExpanded((e) => ({ ...e, [g.key]: !isOpen }))}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
                  activeInGroup ? "text-lm-brown" : "text-lm-muted hover:bg-lm-sandsoft"
                }`}>
                <g.icon className="w-[18px] h-[18px]" />
                <span className="flex-1 text-left">{g.label}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
              </button>
              {isOpen && (
                <div className="mt-1 ml-3 pl-3 border-l border-lm-sandsoft space-y-1">
                  {g.items.map((n) => (
                    <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)}
                      data-testid={testidFor(n.to)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive ? "bg-lm-brown text-lm-cream" : "text-lm-text2 hover:bg-lm-sandsoft"
                        }`
                      }>
                      <n.icon className="w-[16px] h-[16px]" />
                      {n.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {singles.length > 0 && (
          <div className="pt-2 mt-2 border-t border-lm-sandsoft space-y-1">
            {singles.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)}
                data-testid={testidFor(n.to)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-lm-brown text-lm-cream" : "text-lm-text2 hover:bg-lm-sandsoft"
                  }`
                }>
                <n.icon className="w-[18px] h-[18px]" />
                {n.label}
              </NavLink>
            ))}
          </div>
        )}
      </nav>
      <div className="px-4 py-4 border-t border-lm-sandsoft">
        <div className="mb-3">
          <p className="text-sm font-semibold text-lm-text truncate">{user?.name}</p>
          <p className="text-xs text-lm-muted">{ROLES[user?.role]}</p>
        </div>
        <Button variant="outline" size="sm" data-testid="logout-btn"
          onClick={async () => { await logout(); nav("/login"); }}
          className="w-full border-lm-sand text-lm-brown hover:bg-lm-sandsoft">
          <LogOut className="w-4 h-4 mr-2" /> Esci
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-lm-cream">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-[#F4ECE1] border-r border-lm-sandsoft fixed inset-y-0">
        <SideContent />
      </aside>

      {/* Drawer mobile */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-[#F4ECE1] shadow-xl"><SideContent /></aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 backdrop-blur-md bg-lm-cream/90 border-b border-lm-sandsoft">
          <div className="flex items-center justify-between px-4 sm:px-6 h-16">
            <div className="flex items-center gap-3">
              <button className="lg:hidden p-2 -ml-2 text-lm-brown" onClick={() => setOpen(true)} data-testid="menu-toggle">
                <Menu className="w-6 h-6" />
              </button>
              <img src="/logo-mascheroni.webp" alt="LM" className="h-7 w-auto lg:hidden" />
            </div>
            <div className="relative">
              <button data-testid="notifications-btn" onClick={() => setShowNotif((s) => !s)}
                className="relative p-2 rounded-lg hover:bg-lm-sandsoft text-lm-brown">
                <Bell className="w-5 h-5" />
                {unread > 0 && (
                  <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-lm-terra text-white text-[10px] font-bold flex items-center justify-center">
                    {unread}
                  </span>
                )}
              </button>
              {showNotif && (
                <div className="absolute right-0 mt-2 w-80 max-w-[92vw] lm-card p-0 overflow-hidden" data-testid="notif-panel">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-lm-sandsoft">
                    <span className="font-semibold text-sm">Notifiche</span>
                    <div className="flex items-center gap-2">
                      <button onClick={markAll} className="text-xs text-lm-terra hover:underline">Segna lette</button>
                      <button onClick={() => setShowNotif(false)}><X className="w-4 h-4 text-lm-muted" /></button>
                    </div>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifs.length === 0 && <p className="px-4 py-6 text-sm text-lm-muted text-center">Nessuna notifica</p>}
                    {notifs.map((n) => (
                      <div key={n.id} className={`px-4 py-3 border-b border-lm-sandsoft/60 text-sm ${n.read ? "opacity-60" : "bg-lm-sandsoft/30"}`}>
                        <p className="text-lm-text">{n.message}</p>
                        <p className="text-[11px] text-lm-muted mt-1">{new Date(n.created_at).toLocaleString("it-IT")}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-6 py-6 max-w-[1500px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
